import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

interface PickerItemProps {
  value: string;
  isSelected: boolean;
  height: number;
  renderItem: (value: string, selected: boolean) => React.ReactNode;
  onClick: () => void;
}

const PickerItem = memo(function PickerItem({
  value,
  isSelected,
  height,
  renderItem,
  onClick,
}: PickerItemProps) {
  return (
    <div
      style={{
        height,
        flexShrink: 0,
        scrollSnapAlign: "center",
        scrollSnapStop: "normal",
      }}
      onClick={onClick}
    >
      {renderItem(value, isSelected)}
    </div>
  );
});

export interface ScrollPickerColumnExternalProps {
  name: string;
  options: string[];
  renderItem?: (value: string, selected: boolean) => React.ReactNode;
}

export interface ScrollPickerColumnProps extends ScrollPickerColumnExternalProps {
  columnValue: string;
  onColumnChange: (name: string, value: string) => void;
  height: number;
  itemHeight: number;
  highlightColor?: string;
}

const isNative = Capacitor.getPlatform() !== "web";

function triggerHaptic() {
  if (!isNative) return;
  Haptics.impact({ style: ImpactStyle.Light }).catch(() => null);
}

export const ScrollPickerColumn = memo(function ScrollPickerColumn({
  name,
  options,
  renderItem,
  columnValue,
  onColumnChange,
  height,
  itemHeight,
  highlightColor = "#ddc5ff",
}: ScrollPickerColumnProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [selectedIndex, setSelectedIndex] = useState(() => {
    const idx = options.indexOf(columnValue);
    return idx >= 0 ? idx : 0;
  });

  const paddingSize = (height - itemHeight) / 2;

  // Mutable refs so the scroll handler never needs to be reinstalled when values change.
  const onColumnChangeRef = useRef(onColumnChange);
  const columnValueRef = useRef(columnValue);
  const optionsRef = useRef(options);
  const itemHeightRef = useRef(itemHeight);
  useLayoutEffect(() => {
    onColumnChangeRef.current = onColumnChange;
    columnValueRef.current = columnValue;
    optionsRef.current = options;
    itemHeightRef.current = itemHeight;
  });

  // Stable renderItem wrapper so PickerItem memo sees the same function reference even
  // when the caller passes a new inline arrow function on every render.
  const renderItemRef = useRef(renderItem);
  useLayoutEffect(() => {
    renderItemRef.current = renderItem;
  });
  const stableRenderItem = useCallback(
    (v: string, selected: boolean) =>
      renderItemRef.current ? renderItemRef.current(v, selected) : v,
    [],
  );

  // Tracks whether a programmatic scroll is in progress. On iOS inside an animating
  // container (e.g. IonPopover), scrollTo may silently fail on the first call and
  // trigger a spurious scroll event at position 0 — which would make settle emit the
  // first option as the value. While this flag is true, settle is suppressed.
  const isProgrammaticScrollRef = useRef(false);

  // Sync scroll position when columnValue or options change (external change, or options
  // shrink and the current value is no longer valid — e.g. switching month when day was "31").
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let idx = options.indexOf(columnValue);
    if (idx < 0) idx = options.length - 1; // clamp to last valid option

    setSelectedIndex(idx);

    const targetTop = idx * itemHeight;
    if (Math.abs(el.scrollTop - targetTop) < 1) return;

    isProgrammaticScrollRef.current = true;
    el.scrollTo({ top: targetTop, behavior: "instant" });

    // iOS WebKit inside an animating IonPopover may not honour the immediate scrollTo —
    // the animation resets scrollTop to 0 after our call. Retry each frame until the
    // position is correct, then release the suppression flag. 30 retries ≈ 500 ms,
    // which comfortably covers Ionic's default popover enter animation.
    let rafId: number;
    let retries = 0;
    const maxRetries = 30;

    const tryScroll = () => {
      retries++;
      const currentEl = scrollRef.current;
      if (!currentEl) {
        isProgrammaticScrollRef.current = false;
        return;
      }
      if (Math.abs(currentEl.scrollTop - targetTop) >= 1 && retries < maxRetries) {
        currentEl.scrollTo({ top: targetTop, behavior: "instant" });
        rafId = requestAnimationFrame(tryScroll);
      } else {
        isProgrammaticScrollRef.current = false;
      }
    };
    rafId = requestAnimationFrame(tryScroll);

    return () => {
      cancelAnimationFrame(rafId);
      isProgrammaticScrollRef.current = false;
    };
  }, [columnValue, options, itemHeight]);

  // Scroll-settle listener — installed once, reads live values via refs.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let debounceTimer: ReturnType<typeof setTimeout>;
    // Guard so scrollend + debounce can't both fire onChange for the same settle position.
    let lastSettledTop = -1;
    // Tracks the last item index a haptic was fired for during active scrolling.
    let lastHapticIndex = -1;

    const settle = () => {
      // Ignore scrolls triggered by our own programmatic position sync.
      if (isProgrammaticScrollRef.current) return;

      const h = itemHeightRef.current;
      const opts = optionsRef.current;
      const raw = Math.round(el.scrollTop / h);
      const clamped = Math.max(0, Math.min(opts.length - 1, raw));
      const canonicalTop = clamped * h;
      if (canonicalTop === lastSettledTop) return;
      lastSettledTop = canonicalTop;
      setSelectedIndex(clamped);
      const newValue = opts[clamped];
      if (newValue !== columnValueRef.current) {
        onColumnChangeRef.current(name, newValue);
      }
    };

    // Debounce fallback for browsers that don't fire scrollend.
    // Also fires a haptic for every new item boundary the scroll passes through,
    // matching native picker "click-per-item" feedback.
    const onScroll = () => {
      if (!isProgrammaticScrollRef.current) {
        const h = itemHeightRef.current;
        const opts = optionsRef.current;
        const raw = Math.round(el.scrollTop / h);
        const clamped = Math.max(0, Math.min(opts.length - 1, raw));
        if (clamped !== lastHapticIndex) {
          lastHapticIndex = clamped;
          triggerHaptic();
        }
      }
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(settle, 100);
    };

    const supportsScrollEnd = "onscrollend" in window;
    if (supportsScrollEnd) {
      el.addEventListener("scrollend", settle, { passive: true });
    }
    // Always attach debounced scroll as safety net.
    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(debounceTimer);
      if (supportsScrollEnd) el.removeEventListener("scrollend", settle);
      el.removeEventListener("scroll", onScroll);
    };
  }, [name]); // name is stable; everything else is accessed via refs

  // DOM layering:
  //   wrapper (position:relative)
  //   ├── pill  (position:absolute, painted first = behind)
  //   └── scroll container (position:absolute inset-0, transparent bg, painted on top)
  //       └── items (transparent bg — pill shows through)
  //
  // Both pill and scroll are position:absolute so they share the same paint step.
  // Later DOM order wins, putting the scroll container on top of the pill.
  return (
    <div style={{ flex: "1 1 0%", height, position: "relative" }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: paddingSize,
          left: 8,
          right: 8,
          height: itemHeight,
          borderRadius: 40,
          backgroundColor: highlightColor,
          pointerEvents: "none",
        }}
      />
      <div
        ref={scrollRef}
        className="scroll-picker-col"
        style={{
          position: "absolute",
          inset: 0,
          overflowY: "scroll",
          scrollSnapType: "y mandatory",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          WebkitOverflowScrolling: "touch" as any,
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          background: "transparent",
        }}
      >
        <div style={{ height: paddingSize, flexShrink: 0 }} aria-hidden="true" />
        {options.map((opt, idx) => {
          const targetTop = idx * itemHeight;
          return (
            <PickerItem
              key={opt}
              value={opt}
              isSelected={idx === selectedIndex}
              height={itemHeight}
              renderItem={stableRenderItem}
              onClick={() => {
                scrollRef.current?.scrollTo({ top: targetTop, behavior: "smooth" });
              }}
            />
          );
        })}
        <div style={{ height: paddingSize, flexShrink: 0 }} aria-hidden="true" />
      </div>
    </div>
  );
});
